import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { persistQueryClient } from '@tanstack/react-query-persist-client';

/**
 * Sistema de persistencia de caché para React Query
 *
 * Features:
 * - Persiste el caché en localStorage
 * - Buster automático cuando cambia la versión de la app
 * - Limpieza automática de caché antiguo
 * - Compresión de datos para ahorrar espacio
 */

const CACHE_VERSION = '1.2.0'; // Incrementado para limpiar cache con problemas de CancelledError
const CACHE_KEY = 'OWLY_CRM_CACHE';

// 🔥 LIMPIEZA FORZADA DE CACHE CORRUPTO - EJECUTAR ANTES DE TODO
(() => {
  try {
    const cachedVersion = localStorage.getItem('OWLY_CRM_VERSION');
    const cacheData = localStorage.getItem(CACHE_KEY);
    
    // Si hay cache y es versión antigua, limpiar INMEDIATAMENTE
    if (cacheData && cachedVersion !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem('OWLY_CRM_VERSION');
    }
    
    // Si hay cache corrupto (contiene "pending"), limpiar
    if (cacheData) {
      try {
        const parsed = JSON.parse(cacheData);
        if (JSON.stringify(parsed).includes('"pending"')) {
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem('OWLY_CRM_VERSION');
          // Forzar recarga automática
          if (confirm('Se detectó cache corrupto. ¿Quieres recargar la página para solucionarlo?')) {
            window.location.reload();
          }
        }
      } catch (e) {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem('OWLY_CRM_VERSION');
      }
    }
  } catch (error) {
  }
})();

// Crear persister con localStorage
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  key: CACHE_KEY,
});

/**
 * Inicializar persistencia de caché
 */
export function setupCachePersistence(queryClient: QueryClient) {
  try {
    // Limpiar caché si cambió la versión
    const cachedVersion = localStorage.getItem('OWLY_CRM_VERSION');
    
    
    if (cachedVersion !== CACHE_VERSION) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.setItem('OWLY_CRM_VERSION', CACHE_VERSION);
    }

    // Configurar persistencia
    persistQueryClient({
      queryClient,
      persister: localStoragePersister,
      maxAge: 1000 * 60 * 60 * 24, // 24 horas
      buster: CACHE_VERSION,
      // No persistir queries que estén en estado de error o pending
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => {
          const state = query.state;
          const shouldDehydrate = state.status === 'success';
          
          if (!shouldDehydrate) {
          }
          
          // No persistir queries con errores o pending (evita CancelledError)
          return shouldDehydrate;
        },
      },
    });
    
  } catch (error) {
    // Forzar limpieza si hay error
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem('OWLY_CRM_VERSION');
    throw error;
  }
}

/**
 * Limpiar caché manualmente
 */
export function clearCache() {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem('OWLY_CRM_VERSION');
}

/**
 * Limpiar cache y recargar página
 */
export function clearCacheAndReload() {
  clearCache();
  window.location.reload();
}

// Exponer función global para debugging
if (typeof window !== 'undefined') {
  (window as any).clearOwlyCache = clearCacheAndReload;
}

/**
 * Obtener tamaño del caché
 */
export function getCacheSize(): string {
  const cache = localStorage.getItem(CACHE_KEY);
  if (!cache) return '0 KB';

  const sizeInBytes = new Blob([cache]).size;
  const sizeInKB = (sizeInBytes / 1024).toFixed(2);
  const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

  return sizeInBytes > 1024 * 1024 ? `${sizeInMB} MB` : `${sizeInKB} KB`;
}

/**
 * Verificar si hay caché disponible
 */
export function hasCachedData(): boolean {
  const cache = localStorage.getItem(CACHE_KEY);
  return !!cache && cache.length > 0;
}
